import { useLocation, useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import './index.css';
import Navbar from '../Navbar2';
import Booking from '../VillaBooking';
import Aminities from '../Aminities';
import VillaServices from '../VillaServices';
import Contact from '../Contact';
import Before from '../BeforeBook';
import Gallery from '../Gallery';
import HouseRules from '../HouseRules';
import { FaShareAlt } from "react-icons/fa";
import { IoCloudDownload } from "react-icons/io5";
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { toast } from 'react-hot-toast';
import ScrollReveal from '../ScrollReveal';

const VillaDetails = () => {
  const { state } = useLocation();
  const { slug } = useParams();
  const bookingRef = useRef(null);
  const [villa, setVilla] = useState(state?.villa || null);
  const [loading, setLoading] = useState(!state?.villa);
  const [error, setError] = useState(null);
  const [mapError, setMapError] = useState(null);

  const defaultMapLink = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3912.2285902034965!2d76.69502947506103!3d11.41020334736148!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba899255ce5c2cb%3A0x41e2b1e87e77f02f!2sOoty%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1718536320000!5m2!1sen!2sin';

  useEffect(() => {
    if (!villa && slug) {
      const fetchVilla = async () => {
        try {
          setLoading(true);
          const villasCollection = collection(db, 'villas');
          const villasSnapshot = await getDocs(villasCollection);
          const villaData = villasSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .find(v => v.title.toLowerCase().replace(/\s+/g, '-').trim() === slug.trim());

          if (villaData) {
            setVilla(villaData);
          } else {
            setError('Villa not found');
          }
        } catch (err) {
          setError('Failed to load villa details');
        } finally {
          setLoading(false);
        }
      };

      fetchVilla();
    }
  }, [slug, villa]);

  if (loading) {
    return (
      <div className="spinner-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !villa) {
    return (
      <div className="villa-details-container">
        <h2>Villa Not Found</h2>
        <p>{error || 'The villa you are looking for does not exist.'}</p>
        <a href="/" className="back-link">Back to Villas</a>
      </div>
    );
  }

  const distances = villa.distances || [];

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url)
      .then(() => {
        toast.success('Link copied to clipboard!', { duration: 4000 });
      })
      .catch((err) => {
        toast.error('Failed to copy link. Please try again.', { duration: 4000 });
      });
  };

  const handleBookNow = () => {
    if (bookingRef.current) {
      bookingRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isValidMapLink = villa.googleMapLink && villa.googleMapLink.startsWith('https://www.google.com/maps/embed');
  const mapLinkToUse = isValidMapLink ? villa.googleMapLink : defaultMapLink;
  const fallbackMapLink = villa.googleMapLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(villa.location)}`;

  const handleMapLoadError = () => {
    setMapError('Failed to load map. Displaying default location.');
  };

  return (
    <>  
      <div className="villa-details-container">
        <Navbar />
        <div className="villa-image-section">
          <img src={villa.imageUrls[0]} alt={villa.title} className="villa-main-image" />
        </div>

        <div className="cards-section">
          <div className="description-card">
            <div className="villa-info-actions">
              <div className="action-buttons">
                <button className="action-btn" onClick={handleShare}>
                  <span className="icon"><FaShareAlt style={{ color: '#6b2d5c' }} /></span> Share
                </button>
                <button className="action-btn">
                  <span className="icon"><IoCloudDownload style={{ color: '#6b2d5c' }} /></span> Download Brochure
                </button>
                <button className="book-now-btn" onClick={handleBookNow}>
                  BOOK NOW
                </button>
              </div>
              <div className="villa-title-section mt-5">
                <h1 className="villa-title">{villa.title}</h1>
                <p className="villa-location">{villa.location}</p>
                <p className="villa-details">{villa.details}</p>
                <p className="description-text">{villa.ldescription}</p>
              </div>
            </div>
          </div>

          <div className="map-card">
            <iframe
              title="villa-location"
              src={mapLinkToUse}
              className="map-iframe"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              onError={handleMapLoadError}
            ></iframe>
            {mapError || !isValidMapLink ? (
              <div className="map-error-container">
                <p className="map-error">
                  {mapError || 'Showing default map location. Please check the location details or contact support.'}
                </p>
                <a
                  href={fallbackMapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-fallback-link"
                >
                  View {villa.title} location on Google Maps
                </a>
              </div>
            ) : null}
            <p className="map-info">
              {villa.title} is located in {villa.location}, offering easy access to nearby attractions and scenic views.
            </p>
            <ul className="distance-list">
              {distances.map((distance, index) => (
                <li key={index} className="distance-item">
                  <span className="distance-place">{distance.place}:</span> {distance.time}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div ref={bookingRef}>
          <ScrollReveal><Booking villa={villa} dateRange={state?.dateRange} adults={state?.adults} children={state?.children} /></ScrollReveal>
        </div>
      </div>
      <div>
        <ScrollReveal><Aminities /></ScrollReveal>
      </div>
      <ScrollReveal><VillaServices /></ScrollReveal>
      <ScrollReveal><Before /></ScrollReveal>
      <ScrollReveal><HouseRules /></ScrollReveal>
      <ScrollReveal><Gallery /></ScrollReveal>
      <ScrollReveal><Contact /></ScrollReveal>
    </>
  );
};

export default VillaDetails;